import React, { useEffect, useRef, useState } from 'react';
import { Plus, Edit, Trash2, Save, X, Camera, Phone, Mail, FileText, Upload, GripVertical, Users, CheckCircle, AlertCircle } from 'lucide-react';
import { Agent } from '../../types/Agent';

import { uploadMedia } from '../../lib/media';
import { useContentStore } from './hooks/useContentStore';

const AgentManager: React.FC = () => {
  const [agentsData, setAgentsData] = useContentStore<any>('agents', []);
  
  // Normalize legacy object-shaped data to an array.
  const agents = Array.isArray(agentsData) ? agentsData : Object.values(agentsData || {});
  const setAgents = (newAgents: Agent[] | ((prev: Agent[]) => Agent[])) => {
    const agentsArray = typeof newAgents === 'function' ? newAgents(agents) : newAgents;
    setAgentsData(agentsArray);
  };
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [uploadToast, setUploadToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const uploadToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [formData, setFormData] = useState<Partial<Agent>>({
    name: '',
    title: 'Independent Licensed Agent',
    bio: '',
    image: '',
    phone: '',
    email: '',
    planenrollUrl: '',
    specialties: [],
    yearsExperience: 0,
    licenseNumber: ''
  });

  useEffect(() => {
    return () => {
      if (uploadToastTimeoutRef.current) {
        clearTimeout(uploadToastTimeoutRef.current);
      }
    };
  }, []);

  const showUploadToast = (type: 'success' | 'error', message: string) => {
    setUploadToast({ type, message });
    if (uploadToastTimeoutRef.current) {
      clearTimeout(uploadToastTimeoutRef.current);
    }
    uploadToastTimeoutRef.current = setTimeout(() => {
      setUploadToast(null);
    }, type === 'error' ? 5000 : 3000);
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setUploadToast(null);
    setFormData({
      name: '',
      title: 'Independent Licensed Agent',
      bio: '',
      image: '',
      phone: '',
      email: '',
      planenrollUrl: '',
      specialties: [],
      yearsExperience: 0,
      licenseNumber: ''
    });
  };

  const handleEdit = (agent: Agent) => {
    setEditingAgent(agent);
    setUploadToast(null);
    setFormData(agent);
  };

  const handleDelete = (agentId: string) => {
    if (window.confirm('Are you sure you want to delete this agent?')) {
      setAgents(prev => prev.filter(agent => agent.id !== agentId));
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (dragIndex !== dropIndex) {
      const newAgents = [...agents];
      const draggedAgent = newAgents[dragIndex];
      newAgents.splice(dragIndex, 1);
      newAgents.splice(dropIndex, 0, draggedAgent);
      setAgents(newAgents);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isAddingNew) {
      const newAgent: Agent = {
        id: Date.now().toString(),
        name: formData.name || '',
        title: formData.title || 'Independent Licensed Agent',
        bio: formData.bio || '',
        image: formData.image || 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=400',
        phone: formData.phone || '',
        email: formData.email || '',
        planenrollUrl: formData.planenrollUrl || '',
        specialties: formData.specialties || [],
        yearsExperience: formData.yearsExperience || 0,
        licenseNumber: formData.licenseNumber || ''
      };
      setAgents(prev => [...prev, newAgent]);
      setIsAddingNew(false);
    } else if (editingAgent) {
      setAgents(prev => prev.map(agent => 
        agent.id === editingAgent.id ? { ...agent, ...formData } : agent
      ));
      setEditingAgent(null);
    }
    setUploadToast(null);
    setFormData({});
  };

  const handleCancel = () => {
    setEditingAgent(null);
    setIsAddingNew(false);
    setUploadToast(null);
    setFormData({});
  };

  const handleInputChange = (field: keyof Agent, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSpecialtiesChange = (specialtiesText: string) => {
    const specialties = specialtiesText.split(',').map(s => s.trim()).filter(s => s.length > 0);
    handleInputChange('specialties', specialties);
  };

  if (isAddingNew || editingAgent) {
    return (
      <div className="p-6">
        {uploadToast && (
          <div
            className={`fixed top-6 right-6 z-50 flex max-w-sm items-center gap-3 rounded-lg border px-4 py-3 shadow-lg ${
              uploadToast.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
            role={uploadToast.type === 'success' ? 'status' : 'alert'}
            aria-live="polite"
          >
            {uploadToast.type === 'success' ? (
              <CheckCircle size={20} className="flex-shrink-0 text-green-600" />
            ) : (
              <AlertCircle size={20} className="flex-shrink-0 text-red-600" />
            )}
            <span className="font-medium">{uploadToast.message}</span>
          </div>
        )}
        <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200">
          <h1 className="text-3xl font-bold text-gray-900">
            {isAddingNew ? 'Add New Agent' : 'Edit Agent'}
          </h1>
          <button
            onClick={handleCancel}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <X size={20} />
            <span>Cancel</span>
          </button>
        </div>

        <form onSubmit={handleFormSubmit} className="max-w-4xl">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Photo */}
            <div className="space-y-4">
              <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center">
                {formData.image ? (
                  <img 
                    src={formData.image} 
                    alt={formData.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center text-gray-500">
                    <Camera size={48} className="mx-auto mb-2" />
                    <p>No photo uploaded</p>
                  </div>
                )}
              </div>
              
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Upload Photo
                </label>
                <label className="flex items-center justify-center w-full px-4 py-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <Upload size={16} className="mr-2" />
                  <span>Choose File</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        try {
                          const url = await uploadMedia(file);
                          handleInputChange('image', url);
                          showUploadToast('success', 'The photo is ready to use.');
                        } catch {
                          showUploadToast('error', 'We could not upload this photo. Try a smaller image or contact Kyle at kylemirich20@gmail.com.');
                        }
                      }
                    }}
                  />
                </label>
                <div className="text-center text-gray-500 text-sm">or</div>
                <input
                  type="url"
                  value={formData.image || ''}
                  onChange={(e) => handleInputChange('image', e.target.value)}
                  placeholder="Paste image URL"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Right Columns - Form */}
            <div className="lg:col-span-2 space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio
                </label>
                <textarea
                  value={formData.bio || ''}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan Enroll URL
                </label>
                <input
                  type="url"
                  value={formData.planenrollUrl || ''}
                  onChange={(e) => handleInputChange('planenrollUrl', e.target.value)}
                  placeholder="https://www.planenroll.com/medicare/products?purl=Agent-Name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Number
                </label>
                <input
                  type="text"
                  value={formData.licenseNumber || ''}
                  onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                  placeholder="CA lic# 0000000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Specialties (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.specialties?.join(', ') || ''}
                  onChange={(e) => handleSpecialtiesChange(e.target.value)}
                  placeholder="Medicare Advantage, Supplement Plans, Medicare Guidance"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  <Save size={16} />
                  <span>{isAddingNew ? 'Add Team Member' : 'Save Changes'}</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Members</h1>
          <p className="text-gray-600 mt-1">Add or update the people shown on your website</p>
        </div>
        
        <div className="flex items-center space-x-4">

          
          <button
            onClick={handleAddNew}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <Plus size={16} />
            <span>Add Team Member</span>
          </button>
        </div>
      </div>

      <div className="grid gap-6">
        {Array.isArray(agents) && agents.map((agent, index) => (
          <div 
            key={agent.id} 
            className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow duration-200"
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
          >
            <div className="flex items-start space-x-6">
              {/* Drag Handle */}
              <div className="flex-shrink-0 cursor-move text-gray-400 hover:text-gray-600 pt-2">
                <GripVertical size={20} />
              </div>
              
              {/* Agent Photo */}
              <div className="flex-shrink-0">
                <img
                  src={agent.image}
                  alt={agent.name}
                  className="w-24 h-24 rounded-xl object-cover border-2 border-gray-200"
                />
              </div>

              {/* Agent Info */}
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{agent.name}</h3>
                    <p className="text-blue-600 font-medium">{agent.title}</p>
                    <p className="text-gray-600 mt-2 max-w-2xl">{agent.bio}</p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(agent)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                      title="Edit Agent"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(agent.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      title="Delete Agent"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Phone size={16} />
                    <span>{agent.phone}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Mail size={16} />
                    <span>{agent.email}</span>
                  </div>
                  {agent.planenrollUrl && (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <FileText size={16} />
                      <span>Plan Enroll</span>
                    </div>
                  )}
                </div>

                {/* Specialties and License */}
                <div className="flex flex-wrap items-center gap-4 mt-3">
                  <div className="text-sm text-gray-600">
                    {agent.licenseNumber}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {agent.specialties.map((specialty, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {Array.isArray(agents) && agents.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Users size={64} className="mx-auto" />
          </div>
          <h3 className="text-xl font-medium text-gray-600 mb-2">No team members yet</h3>
          <p className="text-gray-500 mb-6">Add the first person you would like to show on the website.</p>
          <button
            onClick={handleAddNew}
            className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 mx-auto"
          >
            <Plus size={16} />
            <span>Add First Team Member</span>
          </button>
        </div>
      )}


    </div>
  );
};

export default AgentManager;
